// Blueprints
import type { Blueprint } from '../../base/blueprints/Blueprint';
import type { AggregateBlueprint } from '../../base/blueprints/AggregateBlueprint';
import type { RootBlueprint } from '../../base/blueprints/RootBlueprint';
import type { PageBlueprint } from '../blueprints/PageBlueprint';
import type { RowBlueprint } from '../blueprints/RowBlueprint';
import { instanceOfRowBlueprint } from '../blueprints/RowBlueprint';

// Traits
import type { HasWidth } from '../../base/traits/HasWidth';

// Managers
import { ClipboardManager } from '../../base/managers/ClipboardManager';
import type { DimensionsManager } from '../../base/managers/DimensionsManager';
import type { EventManager } from '../../base/managers/EventManager';
import { Events } from '../../base/managers/EventManager';
import type { SchemaManager } from '../../base/managers/SchemaManager';
import type { FormLayoutManager } from './FormLayoutManager';

export class FormClipboardManager extends ClipboardManager
{
    private dimensions: DimensionsManager;
    private placeholder: Blueprint = null;

    public constructor(eventManager: EventManager, schemaManager: SchemaManager, layoutManager: FormLayoutManager, dimensionsManager: DimensionsManager)
    {
        super(eventManager, schemaManager, layoutManager);

        this.dimensions = dimensionsManager;

        this.events.subscribe(Events.INSERT, (parent: AggregateBlueprint, component: Blueprint) =>
        {
            this.selectPlaceholder(parent as RowBlueprint, component);
        });
    }

    public override paste(parent: AggregateBlueprint, before: Blueprint = null): void
    {
        super.paste(parent, before);

        if (instanceOfRowBlueprint(parent) && this.dimensions.space(parent) < 0)
        {
            this.dimensions.autoarrange(parent);
        }
    }

    public override canPaste(parent: AggregateBlueprint): boolean
    {
        if (this.mode != 'none' && instanceOfRowBlueprint(parent) && this.source != null)
        {
            if (this.mode == 'copy' && (this.source as HasWidth).minWidth <= this.dimensions.available(parent) && this.schema.parent(parent, this.source.type) === null)
            {
                return true;
            }

            if (this.mode == 'cut' && this.dimensions.available(parent, this.source) >= 0 && this.schema.parent(parent, this.source.type) === null)
            {
                return true;
            }
        }

        return false;
    }

    public selectForm(document: RootBlueprint): void
    {
        this.selectComponent(document);
    }

    public isFormSelected(document: RootBlueprint = null): boolean
    {
        return this.isSelected(document, 'form');
    }

    public selectPage(area: PageBlueprint): void
    {
        this.selectComponent(area);
    }

    public isPageSelected(page: PageBlueprint = null): boolean
    {
        return this.isSelected(page, 'page');
    }

    public selectRow(slot: RowBlueprint): void
    {
        this.selectComponent(slot);
    }

    public isRowSelected(slot: RowBlueprint = null): boolean
    {
        return this.isSelected(slot, 'row');
    }

    public selectPlaceholder(slot: RowBlueprint, component: Blueprint): void
    {
        this.unselect();

        if (this.dimensions.available(slot) > 0)
        {
            if (this.dimensions.space(slot) == 0)
            {
                this.dimensions.rearrange(slot);
            }

            this.selectRow(slot);
            this.placeholder = component;
        }
    }

    public isPlaceholderSelected(slot: RowBlueprint, component: Blueprint): boolean
    {
        return this.isRowSelected(slot) && ((this.placeholder == null && component == null) || (this.placeholder?.id == component?.id));
    }

    public override selectComponent(component: Blueprint): void
    {
        super.selectComponent(component);
        this.placeholder = null;
    }
}
